import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Button,
  Form,
  Table,
  Badge,
  Dropdown,
  Spinner,
  Pagination
} from 'react-bootstrap';
import {
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import DatePicker from 'react-datepicker';
import { toast, ToastContainer } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import {
  fetchUsersOnLeaveToday,
  allocateLeave,
  // fetchAllLeaves,
  fetchActiveLeaves,
  fetchInactiveLeaves,
  fetchWorkAssignedList,
  assignWorkDays,
  fetchCodersForLeaveAllocation,
  fetchCodersForWorkAssignment,
  fetchCodersForWeeklyAvailability,
  updateSelectedLeaveUsers,
  updateSelectedWorkUsers,
  updateSelectedWorkDays,
  updateSearch,
  setShowLeaveModal,
  clearAlreadyOnLeaveCoders
} from './redux/UserAvailabilitySlicer';

import UserLeaveListModal from './UserLeaveListModal';
import LdsRoller from "../../../utils/LdsRoller";

import './UserAvailability.css';

const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const WEEK_DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const UserAvailability = () => {
  const dispatch = useDispatch();
  
  const {
    usersOnLeaveToday,
    leaveAllocation,
    workAssignedList,
    codersLeaveAllocation,
    codersWorkAssignment,
    codersWeeklyAvailability,
    selectedLeaveUsers,
    selectedWorkUsers,
    selectedWorkDays,
    showLeaveModal,
    search,
    alreadyOnLeaveCoders
  } = useSelector((state) => state.himLeaveManagement);

  const [leaveDropdownOpen, setLeaveDropdownOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ from: new Date(), to: new Date() });

  // Debounce
  const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => clearTimeout(timer);
    }, [value, delay]);

    return debouncedValue;
  };

  // Debounces Funtion for Searches
  const debouncedLeaveCoderSearch = useDebounce(search.leaveCoderSearch);
  const debouncedWorkCoderSearch = useDebounce(search.workCoderSearch);
  const debouncedWeeklySearch = useDebounce(search.weeklySearch);

  // Initial Fetching
  useEffect(() => {
    dispatch(fetchUsersOnLeaveToday({ page: 1, limit: 10 }));
    dispatch(fetchActiveLeaves({ page: 1, limit: 10 }));
    dispatch(fetchInactiveLeaves({ page: 1, limit: 10 }));
    dispatch(fetchWorkAssignedList({ page: 1, limit: 10 }));
    dispatch(fetchCodersForLeaveAllocation({ page: 1, limit: 10 }));
    dispatch(fetchCodersForWorkAssignment({ page: 1, limit: 10 }));
    dispatch(fetchCodersForWeeklyAvailability({ page: 1, limit: 10 }));
  }, [dispatch]);

  // Handle Leave and Update the handleLeaveAllocation function:
  const handleLeaveAllocation = (e) => {
      e.preventDefault();
      
      if (selectedLeaveUsers.length === 0) {
        toast.error('Please select at least one user');
        return;
      }
      
      if (!dateRange.from || !dateRange.to) {
        toast.error('Please select leave dates');
        return;
      }
      
      if (dateRange.from > dateRange.to) {
        toast.error('Leave from date cannot be after leave to date');
        return;
      }

      const payload = {
        user_id: selectedLeaveUsers.map(user => user.user_id),
        username: selectedLeaveUsers.map(user => user.username),
        leave_from: dateRange.from.toISOString().split('T')[0],
        leave_to: dateRange.to.toISOString().split('T')[0],
        created_by: sessionStorage.getItem("userId"),
        updated_by: sessionStorage.getItem("userId")
      };

      dispatch(allocateLeave(payload))
        .unwrap()
        .then((response) => {
          toast.success(response?.message || 'Leave allocated successfully');
          //restes data when success
          setLeaveDropdownOpen(false);
          setDateRange({ from: new Date(), to: new Date() });
          // Success case - refetch data
          dispatch(fetchUsersOnLeaveToday({ page: 1, limit: 10 }));
          dispatch(fetchActiveLeaves({ page: 1, limit: 10 }));
          dispatch(fetchInactiveLeaves({ page: 1, limit: 10 }));
        })
        .catch((error) => {
          if (error && typeof error === 'object' &&
            error.message && error.message.includes('existing') &&
            Array.isArray(error.blocked_users)) {
          
          const existingLeaves = error.blocked_users || [];
          
          if (existingLeaves.length > 0) {
            toast.warning(error.message || 'Some leaves already exist');
          }
        } else {
          const errorMessage = typeof error === 'string' ? error :
                              (error?.message || 'Failed to allocate leave');
          toast.error(errorMessage);
        }
          console.error('Leave allocation ended with error:', error);
          dispatch(fetchUsersOnLeaveToday({ page: 1, limit: 10 }));
          dispatch(fetchActiveLeaves({ page: 1, limit: 10 }));
          dispatch(fetchInactiveLeaves({ page: 1, limit: 10 }));
        });
    };

  //WorkDays Assigning to coders
  const handleWorkAssignment = (e) => {
    e.preventDefault();
    if (selectedWorkUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    if (selectedWorkDays.length === 0) {
      toast.error('Please select at least one work day');
      return;
    }

    const payload = {
      user_id: selectedWorkUsers.map(user => user.user_id),
      usernames: selectedWorkUsers.map(user => user.username),
      work_days: selectedWorkDays,
      created_by: sessionStorage.getItem("userId"),
      updated_by: sessionStorage.getItem("userId")
    };

    dispatch(assignWorkDays(payload))
    .unwrap()
    .then((response) => {
      toast.success(response?.message || 'Work days assigned successfully');
      dispatch(updateSelectedWorkUsers([]));
      dispatch(updateSelectedWorkDays([]));
      dispatch(fetchWorkAssignedList({ page: 1, limit: 10 }));
      dispatch(fetchCodersForWeeklyAvailability({ page: 1, limit: 10 }));
    })
    .catch(err => {
      toast.error(err?.message || 'Error While Assigning Work Days');
      console.log(err?.message || 'Error While Assigning Work Days')
    });
  };

  // Handle searches with debounce
  useEffect(() => {
    dispatch(fetchCodersForLeaveAllocation({
      page: 1,
      limit: 10,
      search: debouncedLeaveCoderSearch
    }));
  }, [debouncedLeaveCoderSearch, dispatch]);

  useEffect(() => {
    dispatch(fetchCodersForWorkAssignment({
      page: 1,
      limit: 10,
      search: debouncedWorkCoderSearch
    }));
  }, [debouncedWorkCoderSearch, dispatch]);

  useEffect(() => {
    dispatch(fetchCodersForWeeklyAvailability({
      page: 1,
      limit: 10,
      search: debouncedWeeklySearch
    }));
  }, [debouncedWeeklySearch, dispatch]);

  const handleSearch = (type, value) => {
    dispatch(updateSearch({ [type]: value }));
  };

  // Scrolls
  const handleLeaveAllocationScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5;

    if (isAtBottom && !codersLeaveAllocation.loading && codersLeaveAllocation.hasMore) {
      const nextPage = codersLeaveAllocation.page + 1;
      dispatch(fetchCodersForLeaveAllocation({ 
        page: nextPage, 
        limit: 10, 
        search: search.leaveCoderSearch 
      }));
    }
  };

  const handleWorkAssignmentScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
    
    if (isNearBottom && !codersWorkAssignment.loading && codersWorkAssignment.hasMore) {
      const nextPage = codersWorkAssignment.page + 1;
      dispatch(fetchCodersForWorkAssignment({ 
        page: nextPage, 
        limit: 10, 
        search: search.workCoderSearch
      }));
    }
  };

  const handleWeeklyAvailabilityScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
    
    if (isNearBottom && !codersWeeklyAvailability.loading && codersWeeklyAvailability.hasMore) {
      const nextPage = codersWeeklyAvailability.page + 1;
      dispatch(fetchCodersForWeeklyAvailability({ 
        page: nextPage, 
        limit: 10, 
        search: search.weeklySearch 
      }));
    }
  };

  // Pagination
  const handleLeaveTodayPageChange = (newPage) => {
    dispatch(fetchUsersOnLeaveToday({ 
      page: newPage, 
      limit: usersOnLeaveToday.pagination.limit 
    }));
  };

  const handleLeaveTodayRowsChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    dispatch(fetchUsersOnLeaveToday({ 
      page: 1, 
      limit: newLimit 
    }));
  };

  // Checkbox selectors
  const handleLeaveUserSelect = (user) => {
    const isAlreadyOnLeave = alreadyOnLeaveCoders.some(
      alreadyUser => alreadyUser.user_id === user.user_id
    );
    
    if (isAlreadyOnLeave) {
      return;
    }
    
    const isSelected = selectedLeaveUsers.some(u => u.user_id === user.user_id);
    const newList = isSelected 
      ? selectedLeaveUsers.filter(u => u.user_id !== user.user_id)
      : [...selectedLeaveUsers, user];
    dispatch(updateSelectedLeaveUsers(newList));
  };

  const handleWorkUserSelect = (user) => {
    const isSelected = selectedWorkUsers.some(u => u.user_id === user.user_id);
    const newList = isSelected 
      ? selectedWorkUsers.filter(u => u.user_id !== user.user_id)
      : [...selectedWorkUsers, user];
    dispatch(updateSelectedWorkUsers(newList));
  };

  const handleSelectAllLeaveCoders = (coders) => {
    // Filter out coders that are already on leave
    const availableCoders = coders.filter(coder => 
      !alreadyOnLeaveCoders.some(alreadyUser => alreadyUser.user_id === coder.user_id)
    );
    
    const availableCoderIds = availableCoders.map(c => c.user_id);
    const currentlySelectedAvailableIds = selectedLeaveUsers
      .filter(user => availableCoderIds.includes(user.user_id))
      .map(user => user.user_id);
    
    if (currentlySelectedAvailableIds.length === availableCoderIds.length) {
      const newSelection = selectedLeaveUsers.filter(
        user => !availableCoderIds.includes(user.user_id)
      );
      dispatch(updateSelectedLeaveUsers(newSelection));
    } else {
      // Select all available that not already on leave
      const newSelection = [
        ...selectedLeaveUsers.filter(user => !availableCoderIds.includes(user.user_id)),
        ...availableCoders.map(user => ({
          user_id: user.user_id,
          username: user.username
        }))
      ];
      dispatch(updateSelectedLeaveUsers(newSelection));
    }
  };

  const handleSelectAllWorkCoders = (coders) => {
    if (selectedWorkUsers.length === coders.length) {
      dispatch(updateSelectedWorkUsers([]));
    } else {
      dispatch(updateSelectedWorkUsers(coders.map(user => ({
        user_id: user.user_id,
        username: user.username
      }))));
    }
  };

  const calculateDays = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return 0;
    const from = new Date(dateRange.from);
    const to = new Date(dateRange.to);
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    return Math.floor((to - from) / (1000 * 60 * 60 * 24)) + 1;
  }, [dateRange]);

  const displayWeeklyData = codersWeeklyAvailability.data;
  const displayLeaveTodayData = usersOnLeaveToday.data;

useEffect(() => {
  if (dateRange.from || dateRange.to) {
    if (alreadyOnLeaveCoders.length > 0) {
      dispatch(clearAlreadyOnLeaveCoders());
    }
  }
}, [dateRange.from, dateRange.to, dispatch]);

//Functions for Dates validations
const toInputDate = (date) => {
  if (!date) return '';
  return new Date(date).toISOString().split('T')[0];
};

const fromInputDate = (value) => {
  return value ? new Date(value) : null;
};


  return (
    <>
    <ToastContainer position="top-right" autoClose={3000} />
      <Paper elevation={0} className="ua-container">
      {/* SECTION 1: LEAVE ALLOCATION */}
      <Card className="ua-card ua-section-gap">
        <div className="ua-header d-flex justify-content-between">
          <span className="ua-title">Leave Allocation</span>
          <Button size="sm" onClick={() => dispatch(setShowLeaveModal(true))}>
            Manage Leaves
          </Button>
        </div>

        <Form onSubmit={handleLeaveAllocation} className="ua-leave-form-grid">
          
          {/* Dates Selection */}
          <Form.Group>
            <Form.Label>From</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={toInputDate(dateRange.from)}
              min={toInputDate(new Date())}
              onChange={(e) =>
                setDateRange({
                  ...dateRange,
                  from: fromInputDate(e.target.value)
                })
              }
            />

          </Form.Group>

          <Form.Group>
            <Form.Label>To</Form.Label>
            <Form.Control
                type="date"
                size="sm"
                value={toInputDate(dateRange.to)}
                min={toInputDate(dateRange.from || new Date())}
                onChange={(e) =>
                  setDateRange({
                    ...dateRange,
                    to: fromInputDate(e.target.value)
                  })
                }
              />

          </Form.Group>

          {/* User Selection */}
          <Form.Group>
            <Form.Label>Users</Form.Label>
            <Dropdown
              show={leaveDropdownOpen}
              onToggle={setLeaveDropdownOpen}
              autoClose="outside"
            >
              <Dropdown.Toggle className="ua-dropdown-toggle">
                {selectedLeaveUsers.length
                  ? `${selectedLeaveUsers.length} Users Selected`
                  : 'Select Users'}
              </Dropdown.Toggle>

              <Dropdown.Menu className="ua-custom-dropdown-menu">
                <div className="ua-dropdown-header-section">
                  <div className="ua-dropdown-search-wrapper">
                    <input
                      className="ua-filter-dropdown-search-input"
                      placeholder="Search users..."
                      value={search.leaveCoderSearch}
                      onChange={(e) =>
                        handleSearch('leaveCoderSearch', e.target.value)
                      }
                      autoFocus
                    />
                  </div>

                  <div className="ua-dropdown-select-all-wrapper">
                    <Form.Check
                      type="checkbox"
                      id="select-all-leave"
                      className="ua-custom-checkbox"
                      label={`Select All (${codersLeaveAllocation.data.filter(coder => 
                        !alreadyOnLeaveCoders.some(alreadyUser => alreadyUser.user_id === coder.user_id)
                      ).length || 0} available)`}
                      checked={
                        codersLeaveAllocation.data?.length > 0 &&
                        codersLeaveAllocation.data
                          .filter(coder => 
                            !alreadyOnLeaveCoders.some(alreadyUser => alreadyUser.user_id === coder.user_id)
                          )
                          .every(coder => 
                            selectedLeaveUsers.some(selected => selected.user_id === coder.user_id)
                          )
                      }
                      onChange={() =>
                        handleSelectAllLeaveCoders(codersLeaveAllocation.data)
                      }
                    />
                  </div>
                </div>

                <div
                  className="ua-dropdown-scrollable-list"
                  onScroll={handleLeaveAllocationScroll}
                >
                  {codersLeaveAllocation.loading &&
                  codersLeaveAllocation.data.length === 0 ? (
                    <div className="ua-dropdown-center-loader">
                      <Spinner size="sm" animation="border" variant="dark" />
                      <span>Loading users...</span>
                    </div>
                  ) : (
                    <>
                      {codersLeaveAllocation.data.length === 0 ? (
                        <div className="ua-dropdown-empty">
                          No users found
                        </div>
                      ) : (
                        codersLeaveAllocation.data.map((user) => {
                          const isAlreadyOnLeave = alreadyOnLeaveCoders.some(
                            alreadyUser => alreadyUser.user_id === user.user_id
                          );
                          const isSelected = selectedLeaveUsers.some(
                            selectedUser => selectedUser.user_id === user.user_id
                          );
                          
                          return (
                            <div
                              key={user.user_id}
                              className="ua-dropdown-item-row"
                              onClick={() => handleLeaveUserSelect(user)}
                              style={{ 
                                cursor: isAlreadyOnLeave ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <Form.Check
                                type="checkbox"
                                id={`user-${user.user_id}`}
                                className="ua-custom-checkbox pointer-events-none"
                                checked={isSelected}
                                disabled={isAlreadyOnLeave}
                                onChange={() => {}}
                                label={
                                  <span style={{ 
                                    color: isAlreadyOnLeave ? '#dc3545' : 'inherit'
                                  }}>
                                    {user.username}
                                  </span>
                                }
                              />
                            </div>
                          );
                        })
                      )}

                      {codersLeaveAllocation.loading &&
                        codersLeaveAllocation.data.length > 0 && (
                          <div className="ua-dropdown-bottom-loader">
                            <Spinner size="sm" animation="border" variant="dark" />
                          </div>
                        )}

                      {codersLeaveAllocation.hasMore &&
                        !codersLeaveAllocation.loading && (
                          <div style={{ height: '20px' }} />
                        )}
                    </>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group>
            <Form.Label>Days</Form.Label>
            <Form.Control size="sm" value={calculateDays} readOnly />
          </Form.Group>

          <Form.Group>
            <Form.Label>&nbsp;</Form.Label>
            <Button
              type="submit"
              size="sm"
              className="ua-confirm-btn"
              disabled={leaveAllocation.loading}
            >
              {leaveAllocation.loading ? (
                <Spinner size="sm" animation="border" variant="dark" />
              ) : (
                'Allocate Leave'
              )}
            </Button>
          </Form.Group>
        </Form>
      </Card>

      {/* SECTION 2: WORK DAY ASSIGNMENT */}
      <Card className="ua-card ua-section-gap">
        <div className="ua-header">
          <span className="ua-title">Weekday Assignment</span>
        </div>

        {/* HEADER */}
        <div className="ua-work-top-row">
          <div className="ua-search-container ua-work-search">
            <Form.Control
              size="sm"
              placeholder="Search user..."
              value={search.workCoderSearch}
              onChange={(e) => handleSearch('workCoderSearch', e.target.value)}
            />
          </div>

          <ToggleButtonGroup
            value={selectedWorkDays}
            onChange={(e, value) => dispatch(updateSelectedWorkDays(value))}
            className="ua-weekday-calendar"
          >
            {WEEK_DAYS_SHORT.map((day) => (
              <ToggleButton key={day} value={day.toUpperCase()}>
                {day}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button
            size="lg"
            className="ua-confirm-btn"
            onClick={handleWorkAssignment}
            disabled={workAssignedList.loading}
          >
            {workAssignedList.loading ? (
              <Spinner size="sm" animation="border" variant="dark" />
            ) : (
              'Apply Work Days'
            )}
          </Button>
        </div>

        {/* User list*/}
        <div
          className="ua-scroll-area ua-fixed-height"
          onScroll={handleWorkAssignmentScroll}
          style={{ position: 'relative' }}
        >
          {codersWorkAssignment.loading && codersWorkAssignment.data.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="dark" />
            </div>
          ) : (
            <>
              {codersWorkAssignment.data.length === 0 &&
              !codersWorkAssignment.loading ? (
                <div className="ua-empty-state">No Users found</div>
              ) : (
                <>
                  <div className='ua-select-all-sticky'>
                    <Form.Check
                    className="ua-select-all-checkbox"
                    label={`Select All (${codersWorkAssignment.data.length})`}
                    checked={
                      codersWorkAssignment.data.length > 0 &&
                      selectedWorkUsers.length === codersWorkAssignment.data.length
                    }
                    onChange={() =>
                      handleSelectAllWorkCoders(codersWorkAssignment.data)
                    }
                  />
                  </div>

                  {codersWorkAssignment.data.map((user) => (
                    <Form.Check
                      key={user.user_id}
                      label={user.username}
                      checked={selectedWorkUsers.some(
                        (u) => u.user_id === user.user_id
                      )}
                      onChange={() =>
                        handleWorkUserSelect({
                          user_id: user.user_id,
                          username: user.username
                        })
                      }
                    />
                  ))}
                </>
              )}

              {codersWorkAssignment.loading &&
                codersWorkAssignment.data.length > 0 && (
                  <div className="text-center py-3 border-top">
                    <Spinner size="sm" animation="border" variant="dark" />
                  </div>
                )}

              {codersWorkAssignment.hasMore &&
                !codersWorkAssignment.loading && (
                  <div style={{ height: '30px' }} />
                )}
            </>
          )}
        </div>

      </Card>

      {/* SECTION 3: WEEKLY AVAILABILITY */}
      <Card className="ua-card ua-section-gap">
        <div className="ua-header">
          <span className="ua-title">Weekly Availability</span>
        </div>

        <div className="ua-search-container gap">
          <Form.Control
            size="sm"
            placeholder="Search User..."
            value={search.weeklySearch}
            onChange={(e) => handleSearch('weeklySearch', e.target.value)}
          />
        </div>

        <div
          className="ua-table-scroll-area weekly-availability"
          style={{ position: 'relative', minHeight: '200px' }}
          onScroll={handleWeeklyAvailabilityScroll}
        >
          {codersWeeklyAvailability.loading && codersWeeklyAvailability.data.length === 0 ? (
            <div className="ua-loader-overlay">
              {/* <Spinner animation="border" variant="dark" /> */}
              <LdsRoller />
            </div>
          ) : (
            <Table className="ua-table ua-no-bottom-border align-middle">
              <thead>
                <tr>
                  <th>User</th>
                  {WEEK_DAYS_SHORT.map((day) => (
                    <th key={day} className="text-center">{day}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {codersWeeklyAvailability.data.length === 0 &&
                  !codersWeeklyAvailability.loading && (
                    <tr>
                      <td colSpan={WEEK_DAYS.length + 1} className="text-center py-5">
                        <div className="ua-empty-state">No Users found</div>
                      </td>
                    </tr>
                  )}

                {codersWeeklyAvailability.data.map((coder) => (
                  <tr key={coder.user_id}>
                    <td className="fw-bold">{coder.username}</td>
                    {WEEK_DAYS.map((day) => (
                      <td key={day} className="text-center">
                        {coder.work_days.includes(day) ? '✔' : '✘'}
                      </td>
                    ))}
                  </tr>
                ))}

                {codersWeeklyAvailability.loading && codersWeeklyAvailability.data.length > 0 && (
                  <tr>
                    <td colSpan={WEEK_DAYS.length + 1} className="text-center py-3">
                      <Spinner size="sm" animation="border" variant="dark" className="me-2" />
                    </td>
                  </tr>
                )}

                {codersWeeklyAvailability.hasMore && !codersWeeklyAvailability.loading && (
                  <tr style={{ height: '40px' }}>
                    <td colSpan={WEEK_DAYS.length + 1} className="text-center" />
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      {/* SECTION 4: CODERS ON LEAVE TODAY */}
      <Card className="ua-card ua-section-gap">
        <div className="ua-header">
          <span className="ua-title">
            Leave Summary
            <span className="ms-2 text-muted">
              ({usersOnLeaveToday.pagination.total_records || 0})
            </span>
          </span>
        </div>

        <div className="ua-leave-today-wrapper">
          <div className="ua-leave-today-container">
            {usersOnLeaveToday.loading ? (
              <div className="text-center py-5">
                {/* <Spinner animation="border" /> */}
                <LdsRoller />
              </div>
            ) : displayLeaveTodayData.length === 0 ? (
              <div className="ua-empty-state">No Scheduled Leaves for Today</div>
            ) : (
              <Table className="ua-no-bottom-border">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Username</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayLeaveTodayData.map((user) => (
                    <tr key={user.user_id}>
                      <td>{user.user_id}</td>
                      <td>{user.username}</td>
                      <td><Badge bg="danger">On Leave</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
          
          {/* Pagination for coders On Leave Today */}
          <div className="ua-HimTable-footer">
            <div>
              <Pagination className="HimTable-pagination">
                <Pagination.First 
                  className="HimTable-pagination-li"
                  onClick={() => handleLeaveTodayPageChange(1)}
                  disabled={usersOnLeaveToday.pagination.page <= 1}
                />
                <Pagination.Prev 
                  onClick={() => handleLeaveTodayPageChange(usersOnLeaveToday.pagination.page - 1)}
                  disabled={usersOnLeaveToday.pagination.page <= 1}
                  className="HimTable-prev-btn"
                />
                <Pagination.Item className="HimTable-pagination-li">
                  Page {usersOnLeaveToday.pagination.page} of {usersOnLeaveToday.pagination.total_pages ? usersOnLeaveToday.pagination.total_pages : 1}
                </Pagination.Item>
                <Pagination.Next 
                  onClick={() => handleLeaveTodayPageChange(usersOnLeaveToday.pagination.page + 1)}
                  disabled={usersOnLeaveToday.pagination.page >= usersOnLeaveToday.pagination.total_pages}
                  className="HimTable-next-btn"
                />
                <Pagination.Last className="HimTable-pagination-li"
                  onClick={() => handleLeaveTodayPageChange(usersOnLeaveToday.pagination.total_pages)}
                  disabled={usersOnLeaveToday.pagination.page === usersOnLeaveToday.pagination.total_pages ? usersOnLeaveToday.pagination.total_pages : 1}
                />
              </Pagination>
            </div>
            <div className="HimTable-row-options">
              <Form.Select
                className="HimTable-select"
                value={usersOnLeaveToday.pagination.limit}
                onChange={handleLeaveTodayRowsChange}
              >
                <option value="" disabled className="HimTable-rows-per-page_number">Rows per page</option>
                <option value={10} className="HimTable-rows-per-page">10</option>
                <option value={20} className="HimTable-rows-per-page">20</option>
                <option value={50} className="HimTable-rows-per-page">50</option>
              </Form.Select>
            </div>
          </div>
        </div>
      </Card>

      <UserLeaveListModal
        show={showLeaveModal}
        onClose={() => dispatch(setShowLeaveModal(false))}
      />
    </Paper>
    </>
  );
};

export default UserAvailability;